import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStorage } from '../../hooks/useStorage';
import { useAuditLog } from '../../hooks/useAuditLog';
import { useAdminRole } from '../../hooks/useAdminRole';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../../components/ImageWithFallback';
import * as FiIcons from 'react-icons/fi';

const {
  FiSave,
  FiArrowLeft,
  FiUpload,
  FiAlertCircle,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiX,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiLink,
  FiShield,
  FiTarget,
  FiAward,
  FiUsers,
  FiHash,
} = FiIcons;

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterCategory, setFilterCategory] = useState('all');
  const [syncing, setSyncing] = useState(false);

  const { uploadFile, uploading, error: uploadError } = useStorage();
  const { logAction } = useAuditLog();
  const { canDelete, canManageSettings } = useAdminRole();
  const navigate = useNavigate();

  const emptyMember = {
    name: '',
    position: '',
    age: '',
    date_of_birth: '',
    bio: '',
    number: '',
    image_url: '',
    is_staff: false,
    staff_group: null,
    is_president: false,
    is_featured: false,
    is_published: true,
    is_active: true,
    featured_label: 'ΤΑΛΕΝΤΟ',
    sort_order: 1,
    team_categories: ['Senior'],
    facebook_url: '',
    instagram_url: '',
    website_url: '',
    sportiko_player_id: null,
    last_synced_at: null,
  };

  const [formData, setFormData] = useState(emptyMember);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const teamCategories = [
    { value: 'Senior', label: "Α' Ομάδα (Senior)" },
    { value: 'K17', label: 'Κ17' },
    { value: 'K14', label: 'Κ14' },
    { value: 'K12', label: 'Κ12' },
  ];

  useEffect(() => {
    fetchMembers();
  }, []);

  const sortMembers = (list) =>
    [...list].sort((a, b) => {
      const orderA = Number(a.sort_order ?? 0);
      const orderB = Number(b.sort_order ?? 0);
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || '').localeCompare(b.name || '', 'el');
    });

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('team_members_aox1234')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('is_staff', { ascending: false })
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;

      const processedData = (data || []).map((member) => ({
        ...member,
        team_categories: member.team_categories || ['Senior'],
        is_published: member.is_published !== false,
        is_active: member.is_staff ? true : member.is_active !== false,
        staff_group: member.is_staff ? member.staff_group || 'technical' : null,
        is_president: Boolean(member.is_president),
      }));

      setMembers(processedData);
    } catch (err) {
      console.error('Error fetching team members:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      if (name === 'is_staff') {
        return {
          ...prev,
          is_staff: checked,
          staff_group: checked ? prev.staff_group || 'technical' : null,
          is_president: checked ? prev.is_president : false,
        };
      }

      if (name === 'staff_group') {
        return {
          ...prev,
          staff_group: value,
          is_president: value === 'board' ? prev.is_president : false,
        };
      }

      if (name === 'is_president') {
        return {
          ...prev,
          is_president: checked,
          staff_group: checked ? 'board' : prev.staff_group,
        };
      }

      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  };

  const handleTeamCategoryChange = (categoryValue, checked) => {
    setFormData((prev) => {
      const currentCategories = prev.team_categories || [];

      if (checked) {
        return currentCategories.includes(categoryValue)
          ? prev
          : { ...prev, team_categories: [...currentCategories, categoryValue] };
      }

      return {
        ...prev,
        team_categories: currentCategories.filter((cat) => cat !== categoryValue),
      };
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);

      const fileReader = new FileReader();
      fileReader.onload = (event) => setPreviewUrl(event.target.result);
      fileReader.readAsDataURL(file);
    }
  };

  const handleSyncWithSportiko = async () => {
    if (!canManageSettings) {
      setMessage({
        type: 'error',
        text: 'Μόνο admin ή superadmin μπορεί να εκτελέσει συγχρονισμό.',
      });
      return;
    }

    setSyncing(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error: syncError } = await supabase.rpc(
        'aox1234_sync_players_from_sportiko'
      );

      if (syncError) throw syncError;

      const result = data || {};
      const parts = [
        `${result.linked || 0} συνδέθηκαν`,
        `${result.created || 0} δημιουργήθηκαν`,
        `${result.updated || 0} ενημερώθηκαν`,
      ];

      if (result.conflicts) {
        parts.push(`${result.conflicts} χρειάζονται χειροκίνητο έλεγχο`);
      }

      setMessage({
        type: result.conflicts ? 'warning' : 'success',
        text: `Sync με fin.sportiko.eu ολοκληρώθηκε: ${parts.join(', ')}.`,
      });

      await fetchMembers();
    } catch (err) {
      console.error('Sportiko sync failed:', err);
      setMessage({
        type: 'error',
        text: `Αποτυχία sync με fin.sportiko.eu: ${err.message || 'Άγνωστο σφάλμα'}`,
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      if (!formData.team_categories || formData.team_categories.length === 0) {
        throw new Error('Παρακαλώ επιλέξτε τουλάχιστον μία κατηγορία ομάδας');
      }

      let imageUrl = formData.image_url;

      if (imageFile) {
        let folder = 'players';
        if (formData.is_staff && formData.staff_group === 'board') folder = 'board';
        if (formData.is_staff && formData.staff_group !== 'board') folder = 'staff';

        const uploadedUrl = await uploadFile(imageFile, 'team_logos', `${folder}/`);

        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          throw new Error(uploadError || 'Failed to upload image');
        }
      }

      const normalizedStaffGroup = formData.is_staff
        ? formData.staff_group || 'technical'
        : null;

      const memberData = {
        ...formData,
        image_url: imageUrl,
        age: !formData.is_staff && formData.age ? parseInt(formData.age, 10) : null,
        date_of_birth:
          !formData.is_staff && formData.date_of_birth ? formData.date_of_birth : null,
        number: !formData.is_staff && formData.number ? parseInt(formData.number, 10) : null,
        sort_order: Number.isNaN(parseInt(formData.sort_order, 10))
          ? 1
          : parseInt(formData.sort_order, 10),
        featured_label: formData.featured_label || 'ΤΑΛΕΝΤΟ',
        team_categories: formData.team_categories,
        is_published: formData.is_published !== false,
        is_active: formData.is_staff ? true : formData.is_active !== false,
        staff_group: normalizedStaffGroup,
        is_president:
          Boolean(formData.is_staff) &&
          normalizedStaffGroup === 'board' &&
          Boolean(formData.is_president),
        is_featured: !formData.is_staff && Boolean(formData.is_featured),
        updated_at: new Date().toISOString(),
      };

      if (memberData.is_president) {
        let clearPresidentQuery = supabase
          .from('team_members_aox1234')
          .update({ is_president: false })
          .eq('is_staff', true)
          .eq('staff_group', 'board')
          .eq('is_president', true);

        if (editingId) {
          clearPresidentQuery = clearPresidentQuery.neq('id', editingId);
        }

        const { error: clearPresidentError } = await clearPresidentQuery;
        if (clearPresidentError) throw clearPresidentError;
      }

      let result;

      if (editingId) {
        result = await supabase
          .from('team_members_aox1234')
          .update(memberData)
          .eq('id', editingId);

        await logAction('update', 'team_member', `Updated member: ${formData.name}`, {
          id: editingId,
          staff_group: memberData.staff_group,
          is_president: memberData.is_president,
          sort_order: memberData.sort_order,
          is_active: memberData.is_active,
          is_published: memberData.is_published,
        });
      } else {
        result = await supabase
          .from('team_members_aox1234')
          .insert([{ ...memberData, created_at: new Date().toISOString() }]);

        await logAction('create', 'team_member', `Added member: ${formData.name}`, {
          staff_group: memberData.staff_group,
          is_president: memberData.is_president,
          sort_order: memberData.sort_order,
          is_active: memberData.is_active,
          is_published: memberData.is_published,
        });
      }

      if (result.error) throw result.error;

      setMessage({
        type: 'success',
        text: editingId
          ? 'Τα στοιχεία ενημερώθηκαν επιτυχώς!'
          : 'Το νέο μέλος προστέθηκε επιτυχώς!',
      });

      resetForm();
      await fetchMembers();
    } catch (err) {
      console.error('Error saving member:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα: ${err.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}`,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      alert('Δεν έχετε δικαίωμα διαγραφής.');
      return;
    }

    if (!window.confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το μέλος;')) {
      return;
    }

    try {
      const memberName = members.find((m) => m.id === id)?.name || 'Unknown';
      const { error: deleteError } = await supabase
        .from('team_members_aox1234')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await logAction('delete', 'team_member', `Deleted member: ${memberName}`, { id });
      setMessage({ type: 'success', text: 'Το μέλος διαγράφηκε επιτυχώς!' });
      await fetchMembers();
    } catch (err) {
      console.error('Error deleting member:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα κατά τη διαγραφή: ${err.message}`,
      });
    }
  };

  const handleEdit = (member) => {
    setFormData({
      ...emptyMember,
      ...member,
      date_of_birth: member.date_of_birth || '',
      featured_label: member.featured_label || 'ΤΑΛΕΝΤΟ',
      team_categories: member.team_categories || ['Senior'],
      facebook_url: member.facebook_url || '',
      instagram_url: member.instagram_url || '',
      website_url: member.website_url || '',
      is_published: member.is_published !== false,
      is_active: member.is_staff ? true : member.is_active !== false,
      staff_group: member.is_staff ? member.staff_group || 'technical' : null,
      is_president: Boolean(member.is_president),
      sort_order: member.sort_order ?? 1,
    });

    setEditingId(member.id);
    setShowForm(true);
    setPreviewUrl('');
    setImageFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData(emptyMember);
    setEditingId(null);
    setShowForm(false);
    setImageFile(null);
    setPreviewUrl('');
  };

  const getTeamCategoryLabel = (category) => {
    const teamCategory = teamCategories.find((cat) => cat.value === category);
    return teamCategory ? teamCategory.label : category;
  };

  const filterByTeamCategory = (list) => {
    if (filterCategory === 'all') return list;
    return list.filter(
      (member) =>
        member.team_categories && member.team_categories.includes(filterCategory)
    );
  };

  const boardMembers = useMemo(
    () =>
      sortMembers(
        filterByTeamCategory(
          members.filter((member) => member.is_staff && member.staff_group === 'board')
        )
      ),
    [members, filterCategory]
  );

  const technicalMembers = useMemo(
    () =>
      sortMembers(
        filterByTeamCategory(
          members.filter(
            (member) => member.is_staff && (member.staff_group || 'technical') !== 'board'
          )
        )
      ),
    [members, filterCategory]
  );

  const players = useMemo(
    () => sortMembers(filterByTeamCategory(members.filter((member) => !member.is_staff))),
    [members, filterCategory]
  );

  const formatSyncDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('el-GR');
  };

  const renderOrderBadge = (member, dark = false) => (
    <div
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black ${
        dark ? 'bg-white/15 text-white border border-white/20' : 'bg-primary-50 text-primary-700'
      }`}
      title="Σειρά εμφάνισης"
    >
      <SafeIcon icon={FiHash} className="w-3 h-3" />
      {member.sort_order ?? 0}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Μέλη Ομάδας & Διοίκηση</h1>
                <p className="text-xs text-gray-500">
                  ΔΣ, τεχνικό επιτελείο και παίκτριες με ανεξάρτητη σειρά εμφάνισης
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {canManageSettings && (
                <button
                  type="button"
                  onClick={handleSyncWithSportiko}
                  disabled={syncing}
                  className={`flex items-center px-4 py-2 border border-primary-600 text-primary-700 bg-white hover:bg-primary-50 rounded-md ${
                    syncing ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  title="Συγχρονισμός προφίλ και κατηγοριών από fin.sportiko.eu. Η κατάσταση Ενεργή/Ανενεργή διαχειρίζεται εδώ."
                >
                  <SafeIcon
                    icon={FiRefreshCw}
                    className={`w-5 h-5 mr-2 ${syncing ? 'animate-spin' : ''}`}
                  />
                  {syncing ? 'Syncing...' : 'Sync with fin.sportiko.eu'}
                </button>
              )}

              <button
                onClick={() => {
                  if (showForm) resetForm();
                  else setShowForm(true);
                }}
                className={`flex items-center px-4 py-2 ${
                  showForm ? 'bg-gray-700' : 'bg-primary-600'
                } hover:opacity-90 text-white rounded-md`}
              >
                <SafeIcon icon={showForm ? FiX : FiPlus} className="w-5 h-5 mr-2" />
                {showForm ? 'Ακύρωση' : 'Προσθήκη Μέλους'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700'
                : message.type === 'warning'
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-red-50 text-red-700'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2 flex-shrink-0" />
            {message.text}
          </motion.div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-md bg-red-50 text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-400" />
            <label htmlFor="category-filter" className="text-sm font-medium text-gray-700">
              Φίλτρο κατά κατηγορία:
            </label>
            <select
              id="category-filter"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Όλες οι κατηγορίες</option>
              {teamCategories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            <div className="ml-auto text-xs text-gray-500">
              <strong>Σειρά εμφάνισης:</strong> 1 = πρώτο, 2 = δεύτερο, 3 = τρίτο κ.ο.κ.
            </div>
          </div>
        </div>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-8"
          >
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingId ? 'Επεξεργασία Μέλους' : 'Προσθήκη Νέου Μέλους'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Για ΔΣ/επιτελείο ενεργοποιήστε «Μέλος Διοίκησης / Επιτελείου».
                </p>
              </div>
              {formData.is_staff && formData.staff_group === 'board' && (
                <div className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full bg-primary-50 text-primary-700 font-bold text-sm">
                  <SafeIcon icon={FiShield} className="w-4 h-4" /> Διοικητικό Συμβούλιο
                </div>
              )}
            </div>

            {formData.sportiko_player_id && (
              <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                <div className="flex items-center gap-2 font-medium">
                  <SafeIcon icon={FiLink} className="w-4 h-4" />
                  Συνδεδεμένη με fin.sportiko.eu
                </div>
                <div className="mt-1 break-all text-xs">
                  Sportiko player ID: {formData.sportiko_player_id}
                </div>
                {formData.last_synced_at && (
                  <div className="mt-1 text-xs">
                    Τελευταίο sync: {formatSyncDate(formData.last_synced_at)}
                  </div>
                )}
                <div className="mt-2 text-xs font-medium">
                  Η κατάσταση Ενεργή/Ανενεργή διαχειρίζεται από αυτή τη σελίδα και διατηρείται στο Sportiko sync.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Φωτογραφία</label>
                  <div className="w-full aspect-square bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center border mb-3">
                    <ImageWithFallback
                      src={previewUrl || formData.image_url}
                      alt={formData.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <label className="cursor-pointer bg-white border border-gray-300 rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:bg-gray-50">
                    <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                    {imageFile ? 'Αλλαγή φωτογραφίας' : 'Επιλογή φωτογραφίας'}
                    <input
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Όνομα *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Θέση / Ρόλος *</label>
                      <input
                        type="text"
                        name="position"
                        required
                        value={formData.position}
                        onChange={handleInputChange}
                        placeholder={formData.is_staff ? 'π.χ. Αντιπρόεδρος / Προπονητής' : 'π.χ. Μέσος'}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_staff"
                          checked={Boolean(formData.is_staff)}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 font-semibold text-gray-800">
                          Μέλος Διοίκησης / Επιτελείου
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Αν δεν είναι ενεργό, η εγγραφή θεωρείται παίκτρια.
                      </p>
                    </div>

                    <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
                      <label className="block text-sm font-bold text-primary-900 mb-1">
                        Σειρά Εμφάνισης
                      </label>
                      <input
                        type="number"
                        name="sort_order"
                        min="1"
                        value={formData.sort_order ?? 1}
                        onChange={handleInputChange}
                        className="block w-full border border-primary-200 rounded-md shadow-sm py-2 px-3 bg-white"
                      />
                      <p className="text-xs text-primary-700 mt-1">1 = πρώτο, 2 = δεύτερο, 3 = τρίτο...</p>
                    </div>
                  </div>

                  {formData.is_staff && (
                    <div className="rounded-2xl border border-gray-200 p-5">
                      <h3 className="font-bold text-gray-900 mb-4">Κατηγορία Μέλους</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label
                          className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                            formData.staff_group === 'board'
                              ? 'border-primary-600 bg-primary-50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="staff_group"
                            value="board"
                            checked={formData.staff_group === 'board'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center">
                              <SafeIcon icon={FiShield} className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="font-black text-gray-900">Διοικητικό Συμβούλιο</div>
                              <div className="text-xs text-gray-500">Πρόεδρος, Αντιπρόεδρος, Γραμματέας κ.λπ.</div>
                            </div>
                          </div>
                        </label>

                        <label
                          className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                            formData.staff_group === 'technical'
                              ? 'border-primary-600 bg-primary-50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="staff_group"
                            value="technical"
                            checked={formData.staff_group === 'technical'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-secondary-800 text-white flex items-center justify-center">
                              <SafeIcon icon={FiTarget} className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="font-black text-gray-900">Τεχνικό & Επιστημονικό</div>
                              <div className="text-xs text-gray-500">Προπονητές, γυμναστές, ιατρικό staff κ.λπ.</div>
                            </div>
                          </div>
                        </label>
                      </div>

                      {formData.staff_group === 'board' && (
                        <label className="mt-4 flex items-start gap-3 rounded-xl bg-yellow-50 border border-yellow-200 p-4 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_president"
                            checked={Boolean(formData.is_president)}
                            onChange={handleInputChange}
                            className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                          />
                          <div>
                            <div className="font-black text-yellow-900 flex items-center gap-2">
                              <SafeIcon icon={FiAward} className="w-4 h-4" />
                              Πρόεδρος Διοικητικού Συμβουλίου
                            </div>
                            <p className="text-xs text-yellow-800 mt-1">
                              Ο Πρόεδρος εμφανίζεται με ειδική, μεγαλύτερη προβολή στην ιστοσελίδα. Αν επιλεγεί νέος Πρόεδρος, ο προηγούμενος αποχαρακτηρίζεται αυτόματα.
                            </p>
                          </div>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!formData.is_staff && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ημερομηνία Γέννησης</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth || ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ηλικία</label>
                    <input
                      type="number"
                      name="age"
                      min="0"
                      max="100"
                      value={formData.age ?? ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Αριθμός Φανέλας</label>
                    <input
                      type="number"
                      name="number"
                      min="1"
                      max="99"
                      value={formData.number ?? ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Βιογραφικό</label>
                <textarea
                  name="bio"
                  rows={4}
                  value={formData.bio || ''}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h3 className="text-md font-bold text-gray-900 mb-4">Social Media Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
                    <input
                      type="url"
                      name="facebook_url"
                      value={formData.facebook_url || ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
                    <input
                      type="url"
                      name="instagram_url"
                      value={formData.instagram_url || ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                    <input
                      type="url"
                      name="website_url"
                      value={formData.website_url || ''}
                      onChange={handleInputChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Κατηγορίες Ομάδων *</label>
                <div className="flex flex-wrap gap-4">
                  {teamCategories.map((category) => (
                    <label key={category.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.team_categories || []).includes(category.value)}
                        onChange={(e) =>
                          handleTeamCategoryChange(category.value, e.target.checked)
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{category.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-6 rounded-xl border border-gray-200 p-4">
                {!formData.is_staff && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={Boolean(formData.is_featured)}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">Προβεβλημένη παίκτρια</label>
                  </div>
                )}
                {!formData.is_staff && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active !== false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">Ενεργή παίκτρια</label>
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published !== false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">Δημοσιευμένη στο website</label>
                </div>
              </div>

              {!formData.is_staff && formData.is_featured && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Κείμενο Προβολής</label>
                  <input
                    type="text"
                    name="featured_label"
                    value={formData.featured_label}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex items-center px-5 py-2 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingId ? 'Ενημέρωση' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Board */}
        <div className="rounded-2xl overflow-hidden shadow-md mb-8 border border-primary-800/20">
          <div className="bg-gradient-to-r from-secondary-900 to-primary-800 text-white px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-black">Διοικητικό Συμβούλιο</h2>
                <p className="text-xs text-white/70">Ο Πρόεδρος προβάλλεται ξεχωριστά στο public site.</p>
              </div>
            </div>
            <span className="text-sm font-bold bg-white/10 px-3 py-1.5 rounded-full">
              {boardMembers.length} μέλη
            </span>
          </div>

          <div className="bg-white p-6">
            {boardMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-6">Δεν υπάρχουν μέλη ΔΣ.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {boardMembers.map((member) => (
                  <div
                    key={member.id}
                    className={`rounded-xl p-4 relative group border transition-all ${
                      member.is_president
                        ? 'bg-gradient-to-br from-primary-900 to-primary-700 text-white border-primary-600 shadow-lg'
                        : 'bg-gray-50 border-gray-100 hover:border-primary-200'
                    } ${member.is_published === false ? 'opacity-60' : ''}`}
                  >
                    <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-1.5 bg-white/90 rounded-full hover:bg-white text-blue-600 shadow-sm"
                        title="Επεξεργασία"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="p-1.5 bg-white/90 rounded-full hover:bg-white text-red-600 shadow-sm"
                          title="Διαγραφή"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/30">
                        <ImageWithFallback
                          src={member.image_url}
                          alt={member.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1 pr-8">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {renderOrderBadge(member, member.is_president)}
                          {member.is_president && (
                            <span className="inline-flex items-center gap-1 text-xs font-black text-yellow-300">
                              <SafeIcon icon={FiAward} className="w-3 h-3" /> ΠΡΟΕΔΡΟΣ
                            </span>
                          )}
                        </div>
                        <h3 className="font-black truncate">{member.name}</h3>
                        <p className={member.is_president ? 'text-primary-100 text-sm' : 'text-primary-700 text-sm font-semibold'}>
                          {member.position}
                        </p>
                        {member.is_published === false && (
                          <span className={`mt-1 inline-flex items-center gap-1 text-xs ${member.is_president ? 'text-white/70' : 'text-gray-600'}`}>
                            <SafeIcon icon={FiEyeOff} className="w-3 h-3" /> Μη δημοσιευμένο
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Technical staff */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Τεχνικό & Επιστημονικό Επιτελείο</h2>
                {filterCategory !== 'all' && (
                  <span className="text-sm text-gray-500">{getTeamCategoryLabel(filterCategory)}</span>
                )}
              </div>
            </div>
            <span className="text-sm font-bold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
              {technicalMembers.length} μέλη
            </span>
          </div>

          {technicalMembers.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Δεν υπάρχουν μέλη επιτελείου.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {technicalMembers.map((member) => (
                <div
                  key={member.id}
                  className={`bg-gray-50 border border-gray-100 rounded-xl p-4 relative group hover:border-primary-200 transition-all ${
                    member.is_published === false ? 'opacity-60' : ''
                  }`}
                >
                  <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-1.5 bg-blue-100 rounded-full hover:bg-blue-200 text-blue-600"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(member.id)}
                        className="p-1.5 bg-red-100 rounded-full hover:bg-red-200 text-red-600"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                      <ImageWithFallback
                        src={member.image_url}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1 pr-8">
                      <div className="mb-1">{renderOrderBadge(member)}</div>
                      <h3 className="font-bold text-gray-900 truncate">{member.name}</h3>
                      <p className="text-sm text-primary-700 font-semibold">{member.position}</p>
                      {member.is_published === false && (
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-gray-600">
                          <SafeIcon icon={FiEyeOff} className="w-3 h-3" /> Μη δημοσιευμένο
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Players */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Παίκτριες</h2>
                {filterCategory !== 'all' && (
                  <span className="text-sm text-gray-500">{getTeamCategoryLabel(filterCategory)}</span>
                )}
              </div>
            </div>
            <span className="text-sm font-bold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
              {players.length} παίκτριες
            </span>
          </div>

          {players.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Δεν υπάρχουν παίκτριες.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {players.map((player) => (
                <div
                  key={player.id}
                  className={`bg-gray-50 border border-gray-100 rounded-xl p-4 relative group hover:border-primary-200 transition-all ${
                    player.is_published === false ? 'opacity-60' : ''
                  }`}
                >
                  <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(player)}
                      className="p-1.5 bg-blue-100 rounded-full hover:bg-blue-200 text-blue-600"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(player.id)}
                        className="p-1.5 bg-red-100 rounded-full hover:bg-red-200 text-red-600"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                      <ImageWithFallback
                        src={player.image_url}
                        alt={player.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1 pr-8">
                      <div className="flex flex-wrap gap-1.5 items-center mb-1">
                        {renderOrderBadge(player)}
                        {player.number && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            #{player.number}
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 truncate">{player.name}</h3>
                      <p className="text-sm text-primary-700">{player.position}</p>
                      <div className="flex flex-wrap gap-1.5 items-center mt-2">
                        {player.sportiko_player_id && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            <SafeIcon icon={FiLink} className="w-3 h-3" /> Sportiko
                          </span>
                        )}
                        {player.is_active !== false ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                            Ενεργή
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Ανενεργή
                          </span>
                        )}
                        {player.is_published !== false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <SafeIcon icon={FiEye} className="w-3 h-3" /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                            <SafeIcon icon={FiEyeOff} className="w-3 h-3" /> Unpublished
                          </span>
                        )}
                      </div>
                      {player.last_synced_at && (
                        <p className="mt-1 text-[11px] text-gray-500">
                          Sync: {formatSyncDate(player.last_synced_at)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMembers;